import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Linkedin, Facebook } from "lucide-react";
import { COMPANY, ALL_SERVICES } from "../lib/data";
import { t } from "../lib/i18n";
import { useApp } from "../context/AppContext";

export default function Footer() {
  const { lang } = useApp();
  return (
    <footer className="bg-[#1A1A1A] text-[#F3F0EA] mt-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="Arthniti Financial Services" className="h-12 w-auto object-contain bg-white rounded-md p-2 shadow-lg" />
          </div>
          <p className="text-sm text-white/70 mt-4 leading-relaxed">
            {t(lang, "footer.compliance")}
          </p>
          <p className="text-xs text-white/50 mt-4 italic leading-relaxed">{t(lang, "footer.risk")}</p>
          <div className="flex items-center gap-3 mt-6">
            <a href={COMPANY.social.instagram} target="_blank" rel="noreferrer" data-testid="footer-social-instagram" className="p-2 rounded-full bg-white/5 hover:bg-primary/30 transition"><Instagram className="h-4 w-4" /></a>
            <a href={COMPANY.social.linkedin} target="_blank" rel="noreferrer" data-testid="footer-social-linkedin" className="p-2 rounded-full bg-white/5 hover:bg-primary/30 transition"><Linkedin className="h-4 w-4" /></a>
            <a href={COMPANY.social.facebook} target="_blank" rel="noreferrer" data-testid="footer-social-facebook" className="p-2 rounded-full bg-white/5 hover:bg-primary/30 transition"><Facebook className="h-4 w-4" /></a>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-serif text-base mb-4">{t(lang, "footer.office")}</h4>
          <div className="space-y-3 text-sm text-white/70">
            <div className="flex gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary shrink-0" /><span>{COMPANY.address}</span></div>
            <div className="flex gap-2"><Phone className="h-4 w-4 mt-0.5 text-primary shrink-0" /><a href={`tel:${COMPANY.phone}`} data-testid="footer-phone-link">{COMPANY.phoneDisplay}</a></div>
            <div className="flex gap-2"><Mail className="h-4 w-4 mt-0.5 text-primary shrink-0" /><a href={`mailto:${COMPANY.email}`} data-testid="footer-email-link" className="break-all">{COMPANY.email}</a></div>
          </div>
        </div>

        <div className="md:col-span-3">
          <h4 className="font-serif text-base mb-4">Services</h4>
          <ul className="space-y-2 text-sm text-white/70">
            {ALL_SERVICES.filter(s => !s.hidden).slice(0, 8).map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-primary transition" data-testid={`footer-service-${s.slug}`}>{s.title}</Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="md:col-span-2">
          <h4 className="font-serif text-base mb-4">{t(lang, "footer.links")}</h4>
          <ul className="space-y-2 text-sm text-white/70">
            <li><Link to="/disclaimer" data-testid="footer-disclaimer" className="hover:text-primary">{t(lang, "footer.disclaimer")}</Link></li>
            <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-primary">{t(lang, "footer.privacy")}</Link></li>
            <li><Link to="/terms" data-testid="footer-terms" className="hover:text-primary">{t(lang, "footer.terms")}</Link></li>
            <li><Link to="/grievance" data-testid="footer-grievance" className="hover:text-primary">{t(lang, "footer.grievance")}</Link></li>
            <li><a href={COMPANY.amfi} target="_blank" rel="noreferrer" data-testid="footer-amfi" className="hover:text-primary">AMFI India</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <p data-testid="footer-partnership-disclaimer" className="text-xs text-white/65 italic leading-relaxed max-w-5xl">
            Arthniti Financial Services provides a wide range of financial solutions by partnering with leading third-party institutions and AMCs. While the product infrastructure is powered by our partners, the expertise, personalized guidance, and service experience are exclusively provided by the Arthniti team.
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-2 text-xs text-white/50">
          <span>{t(lang, "footer.copyright")}</span>
          <span>AMFI-Registered Mutual Fund Distributor • {COMPANY.arn}</span>
        </div>
      </div>
    </footer>
  );
}
